import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../utils/api';

const NotificationContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Plays a two-tone chime using the Web Audio API (no file needed). */
function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1320, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.45);
  } catch (_) {}
}

export function NotificationProvider({ children }) {
  const { user, token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem('notif_sound') !== 'off'; } catch { return true; }
  });
  const esRef = useRef(null);
  const reconnectTimer = useRef(null);

  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem('notif_sound', next ? 'on' : 'off'); } catch {}
      return next;
    });
  }, []);

  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';

  const addNotification = useCallback((notif, playSound = true) => {
    setNotifications(prev => {
      if (prev.some(n => n.id === notif.id)) return prev;
      return [notif, ...prev].slice(0, 50);
    });
    if (!notif.read) {
      setUnreadCount(c => c + 1);
      if (playSound) {
        setSoundEnabled(enabled => {
          if (enabled) playChime();
          return enabled;
        });
      }
    }
  }, []);

  const initNotifications = useCallback((notifs) => {
    setNotifications(notifs.slice(0, 50));
    setUnreadCount(notifs.filter(n => !n.read).length);
  }, []);

  // SSE connection
  useEffect(() => {
    if (!isManagerOrAdmin || !token) return;

    const handleSSELine = (line) => {
      if (!line.startsWith('data: ')) return;
      try {
        const event = JSON.parse(line.slice(6));
        if (event.type === 'init') initNotifications(event.notifications || []);
        else if (event.type === 'new_notification') addNotification(event.notification, true);
      } catch (_) {}
    };

    const readStream = (reader, decoder, onDone) => {
      let buffer = '';
      const read = () => {
        reader.read().then(({ done, value }) => {
          if (done) { onDone(); return; }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();
          lines.forEach(handleSSELine);
          read();
        }).catch(onDone);
      };
      read();
    };

    const connect = () => {
      if (esRef.current) esRef.current.close();
      const controller = new AbortController();

      fetch(`${BACKEND_URL}/api/notifications/stream`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      }).then(response => {
        if (!response.ok) throw new Error(`SSE ${response.status}`);
        setConnected(true);
        readStream(
          response.body.getReader(),
          new TextDecoder(),
          () => { setConnected(false); scheduleReconnect(); }
        );
        esRef.current = { close: () => controller.abort() };
      }).catch(() => {
        setConnected(false);
        scheduleReconnect();
      });

      return controller;
    };

    const scheduleReconnect = () => {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = setTimeout(connect, 5000);
    };

    const controller = connect();
    return () => {
      controller?.abort?.();
      clearTimeout(reconnectTimer.current);
      if (esRef.current) esRef.current.close();
      setConnected(false);
    };
  }, [isManagerOrAdmin, token, addNotification, initNotifications]);

  const markAllRead = useCallback(async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  }, []);

  const markOneRead = useCallback(async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  }, []);

  const clearRead = useCallback(async () => {
    try {
      await api.delete('/notifications/clear');
      setNotifications(prev => prev.filter(n => !n.read));
    } catch {}
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      connected,
      soundEnabled,
      toggleSound,
      markAllRead,
      markOneRead,
      clearRead,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) return {
    notifications: [], unreadCount: 0, connected: false,
    soundEnabled: true, toggleSound: () => {},
    markAllRead: () => {}, markOneRead: () => {}, clearRead: () => {},
  };
  return ctx;
}
